import olMap from 'ol/Map'
import { defaults as olInteractionDefaults } from 'ol/interaction'
import olLayerTile from 'ol/layer/Tile'
import olSourceXYZ from 'ol/source/XYZ'
import olView from 'ol/View'
import olVectorLayer from 'ol/layer/Vector'
import olTileLayer from 'ol/layer/Tile'
import olVectorSource from 'ol/source/Vector'
import olStyle from 'ol/style/Style'
import olStroke from 'ol/style/Stroke'
import olMVT from 'ol/format/MVT'
import olVectorTileLayer from 'ol/layer/VectorTile'
import olVectorTileSource from 'ol/source/VectorTile'
import { easeOut } from 'ol/easing'
import Fill from 'ol/style/Fill'
import { transform, transformExtent } from 'ol/proj'
import { Icon, Style, Text, Circle as CircleStyle, Stroke } from 'ol/style'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import GeoJSON from 'ol/format/GeoJSON'
import Overlay from 'ol/Overlay'
import { Zoom as olZoom, Control } from 'ol/control'
import moment from 'moment'
import { camelCase } from 'lodash'
import { StationListItem, StationGeometry } from '@/models/InitData'
import { useInitDataStore } from '@/stores/initDataStore'
import { useDojouMeshStore } from '@/stores/dojouMeshStore'
import { usePresentStore } from '@/stores/presentStore'

type HomeControlOptions = {
  target: string | HTMLElement
  mapType?: string
}

let defaultLongitude: number
let defaultLatitude: number

const DEFAULT_OPACITY = 0.5

class HomeControl extends Control {
  constructor(options = {} as HomeControlOptions) {
    const button = document.createElement('button')
    const element = document.createElement('div')
    element.className = 'ol-control custom-home'
    element.appendChild(button)

    super({
      element: element,
      target: options.target,
    })

    button.addEventListener(
      'click',
      () => {
        this.onClick()
      },
      false
    )
  }

  onClick() {
    const initDataStore = useInitDataStore()
    let zoom = initDataStore.mapInfo.position.zoom.default
    const isNotMapEarthquakeOL = !document.getElementById('mapEarthquakeOL')

    if (!isNotMapEarthquakeOL) {
      zoom = initDataStore.definitionInfoEarthquake.rangeLimit.zoom.default
    }

    this.getMap()?.getView().setZoom(zoom)

    this.getMap()
      ?.getView()
      .setCenter(transform([defaultLongitude, defaultLatitude], 'EPSG:4326', 'EPSG:3857'))
  }
}

type limitType = {
  defaultLimitLeftUpLatitude: number
  defaultLimitLeftUpLongitude: number
  defaultLimitRightDownLatitude: number
  defaultLimitRightDownLongitude: number
  defaultLimitZoomDefault: number
  defaultLimitZoomMax: number
  defaultLimitZoomMin: number
  defaultLatitude: number
  defaultLongitude: number
}

type EarthquakeStationListItem = {
  code: string
  intensity: string
  latitude: number
  longitude: number
  name: string
}
export type GpvLayerName = 'radar_rain' | 'radar_disaster' | 'radar_soilwater' | 'hazard' | ''

export class OlMap {
  public map?: olMap
  private highlightTimer?: number
  private forecastTime = ''
  private baseTime = ''
  private visibleGpvLayer: GpvLayerName = ''
  public iconSource = new olVectorSource({})
  private iconLayer = new olVectorLayer({
    source: this.iconSource,
    style: new olStyle({
      stroke: new olStroke({
        color: 'rgba(0, 0, 0, 0)',
        width: 2,
      }),
      fill: new Fill({
        color: 'rgba(85, 85, 80, 0.1)',
      }),
    }),
  })

  private iconHighlightSource = new olVectorSource({})
  private iconHighlightLayer = new olVectorLayer({
    source: this.iconHighlightSource,
  })

  private iconHoverListener?: (station?: StationListItem, position?: number[]) => void
  private iconClickListener?: (station?: StationListItem) => void

  // 雨量のレイヤ
  private rainSource = new olVectorTileSource({
    format: new olMVT({}),
    minZoom: 5,
    maxZoom: 9,
    url: `/api/gpv/nowcast/{x}/{y}/{z}?forecast_time=${this.forecastTime}&base_time=${this.baseTime}`,
  })
  private rainLayer = new olVectorTileLayer({
    source: this.rainSource,
    minZoom: 5,
    maxZoom: 18,
    style: feature => {
      const color = this.getRainColor(feature.getProperties().val)
      return new Style({
        stroke: new olStroke({
          color,
        }),
        fill: new Fill({
          color,
        }),
      })
    },
    opacity: 0.5,
  })

  // 土砂のレイヤ
  private doshaSource = new olVectorTileSource({
    format: new olMVT({}),
    minZoom: 5,
    maxZoom: 9,
    url: `/api/gpv/dosha/{x}/{y}/{z}?forecast_time=${this.forecastTime}&base_time=${this.baseTime}`,
  })
  private doshaLayer = new olVectorTileLayer({
    source: this.doshaSource,
    minZoom: 5,
    maxZoom: 18,
    style: feature => {
      const color = this.getDoshaColor(feature.getProperties().val)
      return new Style({
        stroke: new olStroke({
          color,
        }),
        fill: new Fill({
          color,
        }),
      })
    },
    opacity: 0.5,
  })

  // 雨量土壌指数レイヤ
  private dojouMeshSource = new olVectorSource({})
  private dojouMeshLayer = new olVectorLayer({
    source: this.dojouMeshSource,
    style: new olStyle({
      stroke: new olStroke({
        color: 'rgba(0, 0, 0, 0.1)',
        width: 2,
      }),
      fill: new Fill({
        color: '#fff8c5cc',
      }),
    }),
  })

  private areaBoundlyLayer = new olVectorLayer({
    source: new olVectorSource(),
    style: new Style({
      stroke: new olStroke({
        color: 'red',
        width: 3,
      }),
    }),
  })

  private hazardLayers = [] as olTileLayer<olSourceXYZ>[]

  initHazardMap() {
    // ハザードマップ
    const HAZARD_TYPES = [
      'https://disaportaldata.gsi.go.jp/raster/01_flood_l2_shinsuishin_data/{z}/{x}/{y}.png',
      'https://disaportaldata.gsi.go.jp/raster/01_flood_l1_shinsuishin_newlegend_data/{z}/{x}/{y}.png',
      'https://disaportaldata.gsi.go.jp/raster/03_hightide_l2_shinsuishin_data/{z}/{x}/{y}.png',
      'https://disaportaldata.gsi.go.jp/raster/04_tsunami_newlegend_data/{z}/{x}/{y}.png',
    ]

    HAZARD_TYPES.forEach((url: string) => {
      const hazardSource = new olSourceXYZ({
        minZoom: 2,
        maxZoom: 17,
        url,
      })
      this.hazardLayers.push(
        new olTileLayer({
          source: hazardSource,
          minZoom: 5,
          maxZoom: 18,
          opacity: 0.8,
        })
      )
    })
  }

  initAreaBoundary() {
    const initDataStore = useInitDataStore()
    const geojsonPath = initDataStore.mapInfo.geojson

    fetch(geojsonPath)
      //レスポンスのボディーからJSONデータを取得
      .then(response => response.json())
      .then(geojson => {
        this.areaBoundlyLayer.getSource().addFeatures(new GeoJSON().readFeatures(geojson))
      })
  }

  initDojouMeshLayer() {
    const initDataStore = useInitDataStore()
    const geojsonPath = initDataStore.initData.definitionInfo.top.graphical?.soilwater?.geojson.path

    if (geojsonPath) {
      fetch(geojsonPath)
        //レスポンスのボディーからJSONデータを取得
        .then(response => response.json())
        .then(geojson => {
          this.dojouMeshLayer.getSource().addFeatures(new GeoJSON().readFeatures(geojson))
        })
    }
  }

  init(targetId: string, limit: limitType, mapSource: string) {
    const initDataStore = useInitDataStore()
    const extent = [
      limit.defaultLimitLeftUpLongitude,
      limit.defaultLimitLeftUpLatitude,
      limit.defaultLimitRightDownLongitude,
      limit.defaultLimitRightDownLatitude,
    ]

    defaultLongitude = limit.defaultLongitude
    defaultLatitude = limit.defaultLatitude

    this.initAreaBoundary()
    this.initDojouMeshLayer()
    this.initHazardMap()

    this.map = new olMap({
      target: targetId,
      controls: [
        new olZoom({
          zoomInLabel: '',
          zoomOutLabel: '',
          className: 'custom-zoom',
        }),
        new HomeControl(),
      ],
      interactions: olInteractionDefaults({
        altShiftDragRotate: false,
        pinchRotate: false,
      }),
      layers: [
        new olLayerTile({
          source: new olSourceXYZ({
            url: mapSource,
            projection: 'EPSG:3857',
          }),
        }),
        this.rainLayer,
        this.doshaLayer,
        this.dojouMeshLayer,
        ...this.hazardLayers,
        this.areaBoundlyLayer,
        this.iconHighlightLayer,
        this.iconLayer,
      ],
      overlays: [],
      view: new olView({
        projection: 'EPSG:3857',
        center: transform([limit.defaultLongitude, limit.defaultLatitude], 'EPSG:4326', 'EPSG:3857'),
        extent: transformExtent(extent, 'EPSG:4326', 'EPSG:3857'),
        maxZoom: limit.defaultLimitZoomMax,
        minZoom: limit.defaultLimitZoomMin,
        zoom: limit.defaultLimitZoomDefault,
      }),
    })

    const opacity = initDataStore.initData.systemInfo.mapInfo?.opacity || DEFAULT_OPACITY

    this.rainLayer.setVisible(false)
    this.rainLayer.setOpacity(opacity)
    this.doshaLayer.setVisible(false)
    this.doshaLayer.setOpacity(opacity)
    this.dojouMeshLayer.setVisible(false)
    this.hazardLayers.forEach(l => l.setVisible(false))

    this.map.on('pointermove', event => {
      if (!this.map) {
        return
      }

      let f = null
      let shown = false
      this.map.forEachFeatureAtPixel(event.pixel, feature => {
        f = feature
        // アイコンのときだけポップアップ表示
        if (feature.get('data')?.stationId) {
          const geom = feature.getGeometry() as Point
          const position = geom.getCoordinates()

          this.iconHoverListener && this.iconHoverListener(feature.get('data'), position)
          shown = true
        } else {
          !shown && this.iconHoverListener && this.iconHoverListener()
        }
      })

      if (!f && this.iconHoverListener) {
        this.iconHoverListener()
      }
    })

    this.map.on('click', event => {
      if (!this.map) {
        return
      }

      let f = null
      this.map.forEachFeatureAtPixel(event.pixel, feature => {
        f = feature
        // アイコンのときだけポップアップ表示
        if (feature.get('data')?.stationId) {
          if (this.iconClickListener) {
            this.iconClickListener(feature.get('data'))
          }
        }
      })

      if (!f && this.iconClickListener) {
        this.iconClickListener()
      }
    })
  }

  onIconHover(func: (station?: StationListItem, position?: number[]) => void) {
    this.iconHoverListener = func
  }

  onIconClick(func: (station?: StationListItem) => void) {
    this.iconClickListener = func
  }

  destory() {
    if (!this.map) {
      return
    }
    this.map.setTarget()
  }

  setStation(icon: string, station: StationGeometry) {
    const text =
      station.no !== undefined
        ? new Text({
            scale: 1.5,
            fill: new Fill({ color: '#000' }),
            textAlign: 'center',
            text: station.no?.toString() || '',
          })
        : undefined

    const iconStyle = new Style({
      image: new Icon({
        src: icon,
      }),
      text,
    })

    const offset = station.offset
    const longitude = station.longitude + (offset ? offset.x : 0)
    const latitude = station.latitude + (offset ? offset.y : 0)
    const iconFeature = new Feature({
      geometry: new Point(transform([longitude, latitude], 'EPSG:4326', 'EPSG:3857')),
      name: station.name,
      data: station,
    })

    iconFeature.setStyle(iconStyle)
    iconFeature.setId(station.stationId)

    this.iconSource.addFeature(iconFeature)
  }

  getIconUrl(station: StationGeometry, field: string) {
    const initDataStore = useInitDataStore()
    const fieldKey = camelCase(field)
    const presentStore = usePresentStore()
    const iconInfo = station.iconInfo ? station.iconInfo[camelCase(field)] : initDataStore.initData.iconInfo[fieldKey]
    const iconInfoItems = iconInfo.items
    const currentValue = presentStore.getValue(station.stationId, field) || 0
    const currentAlertLevel = (presentStore.getAlertLevel(station.stationId, field) || '').toString()

    // データサマリ用
    if (station.no !== undefined) {
      return 'images/map/icon-summary-rain.svg'
    }

    // カメラ
    if (field == 'camera') {
      return initDataStore.initData.iconInfo.camera?.normal?.path || 'images/map/camera_icon.png'
    }

    for (let i = iconInfoItems.length - 1; i > 0; i--) {
      const item = iconInfoItems[i]

      if (camelCase(currentAlertLevel) === camelCase(item.status)) {
        return item.path
      }

      if (item.comparison && currentValue >= item.comparison) {
        return item.path
      }
    }

    const defaultPath = field == 'water_level' ? 'images/map/water_icon.png' : 'images/map/rain_icon.png'

    return iconInfo.normal?.path || defaultPath
  }

  setPopups(stations: StationGeometry[]) {
    this.map
      ?.getOverlays()
      .getArray()
      .slice(0)
      .forEach(o => {
        this.map?.removeOverlay(o)
      })

    stations.forEach(station => {
      const elementId = `popup-${station.stationId}`
      const element = document.getElementById(elementId)
      const overlay = new Overlay({
        autoPan: {
          animation: {
            duration: 250,
          },
        },
      })

      overlay.set('station', station)
      overlay.setElement(element as HTMLElement)

      this.map?.addOverlay(overlay)
    })

    this.hidePopups()
  }

  hidePopups() {
    this.map?.getOverlays().forEach(o => {
      o.setPosition(undefined)
    })
  }

  showPopups() {
    this.map?.getOverlays().forEach(o => {
      const station = o.get('station')

      this.showPopup(station, true)
    })
  }

  showPopup(station: StationGeometry, all = false) {
    const offset = station.popupOffset
    const lon = station.longitude + (offset ? offset.x : 0)
    const lat = station.latitude + (offset ? offset.y : 0)
    const position = transform([lon, lat], 'EPSG:4326', 'EPSG:3857')

    this.map?.getOverlays().forEach(o => {
      if (o.get('station').stationId === station.stationId) {
        o.setPosition(position)
      } else {
        !all && o.setPosition(undefined)
      }
    })
  }

  setStations(stationList: StationGeometry[], field: string) {
    if (!this.map) {
      return
    }

    this.iconSource.clear()

    const filterStationList = stationList.filter(station => {
      return station.fieldId.includes(field) || field === 'camera'
    }) as StationListItem[]

    filterStationList.forEach(station => {
      const icon = this.getIconUrl(station, field)

      this.setStation(icon, station)
    })
  }

  /**
   * 震源地にアイコンを設定し、観測地点に震度を表示します
   * @param longitude number 震源地の座標
   * @param latitude number 震源地の座標
   * @param stationList array 震度を表示するエリア
   */
  setEarthquakeStations(longitude: number, latitude: number, stationList: EarthquakeStationListItem[]) {
    if (!this.map) {
      return
    }

    this.iconSource.clear()

    if (stationList.length > 0) {
      const sorted_stationList = stationList.sort(function (a, b) {
        return Number(a.intensity) < Number(b.intensity) ? -1 : 1
      })
      for (const station of sorted_stationList) {
        let zIndex = 1
        const intensity = parseInt(station.intensity.replace('-', '').replace('+', ''))
        if (intensity > 1) {
          zIndex = intensity
        }
        const icon_name = `images/map/earthquake_depth_icon_${station.intensity
          .replace('-', 'minus')
          .replace('+', 'plus')}.svg`
        const iconStyle = new Style({
          image: new Icon({
            src: icon_name,
          }),
          zIndex: zIndex,
        })

        const iconFeature = new Feature({
          geometry: new Point(transform([station.longitude, station.latitude], 'EPSG:4326', 'EPSG:3857')),
          name: station.name,
          data: station,
        })

        iconFeature.setStyle(iconStyle)

        this.iconSource.addFeature(iconFeature)
      }
    }

    if (longitude > 0 && latitude > 0) {
      const iconStyle = new Style({
        image: new Icon({
          src: 'images/icon/icon_epicenter.svg',
        }),
        zIndex: 10,
      })

      const iconFeature = new Feature({
        geometry: new Point(transform([longitude, latitude], 'EPSG:4326', 'EPSG:3857')),
      })

      iconFeature.setStyle(iconStyle)

      this.iconSource.addFeature(iconFeature)
    }
  }

  setGpvTime(baseTime: string, forecastTime: string) {
    this.baseTime = baseTime
    this.forecastTime = forecastTime

    if (this.baseTime && this.forecastTime) {
      let baseTime = this.baseTime

      if (this.baseTime > this.forecastTime) {
        baseTime = this.forecastTime
      }
      if (moment(this.forecastTime).diff(this.baseTime, 'minute') > 60) {
        this.rainSource.setUrl(`/api/gpv/srf/{x}/{y}/{z}?forecast_time=${this.forecastTime}&base_time=${baseTime}`)
      } else {
        this.rainSource.setUrl(`/api/gpv/nowcast/{x}/{y}/{z}?forecast_time=${this.forecastTime}&base_time=${baseTime}`)
      }
      this.doshaSource.setUrl(`/api/gpv/dosha/{x}/{y}/{z}?forecast_time=${this.forecastTime}&base_time=${baseTime}`)
    }
  }

  setVisibleGpvLayer(layerName: GpvLayerName) {
    this.visibleGpvLayer = layerName
    this.rainLayer.setVisible(false)
    this.doshaLayer.setVisible(false)
    this.dojouMeshLayer.setVisible(false)
    this.hazardLayers.forEach(l => l.setVisible(false))

    if (this.baseTime && this.forecastTime) {
      if (this.visibleGpvLayer == 'radar_rain') {
        this.rainLayer.setVisible(true)
      } else if (this.visibleGpvLayer == 'radar_disaster') {
        this.doshaLayer.setVisible(true)
      } else if (this.visibleGpvLayer === 'radar_soilwater') {
        this.dojouMeshLayer.setVisible(true)
      }
    }

    if (this.visibleGpvLayer.includes('hazard')) {
      const index = Number(this.visibleGpvLayer.split('_')[1])
      this.hazardLayers[index].setVisible(true)
    }
  }

  getRainColor(val: number) {
    let color = 'rgba(0,0,0,0)'
    if (val > 80) {
      color = '#7F408C'
    } else if (val > 50) {
      color = '#E63922'
    } else if (val > 30) {
      color = '#EE9300'
    } else if (val > 20) {
      color = '#FFEC00'
    } else if (val > 10) {
      color = '#0351B5'
    } else if (val > 5) {
      color = '#009BFF'
    } else if (val > 1) {
      color = '#A1E7FF'
    } else if (val > 0) {
      color = '#FFFFFF'
    }
    return color
  }

  // 土砂の色を指定
  getDoshaColor(val: number) {
    let color = 'rgba(0,0,0,0)'

    if (val >= 4) {
      color = '#0c000c'
    } else if (val >= 3) {
      color = '#aa00aa'
    } else if (val >= 2) {
      color = '#ff2800'
    } else if (val >= 1) {
      color = '#f2e700'
      // } else if (val > 1) {
      //   color = '#FFFFFF'
      // } else if (val > 0) {
      //   color = '#FFFFFF'
    }

    return color
  }

  setPosition(lon: number, lat: number, zoom: number) {
    this.map?.getView().setCenter(transform([lon, lat], 'EPSG:4326', 'EPSG:3857'))
    this.map?.getView().setZoom(zoom)
  }

  setDojouTexts() {
    const dojouMeshStore = useDojouMeshStore()

    // 再描画のためテキストのクリア
    this.dojouMeshSource.forEachFeature(feature => {
      const geometryName = feature.getGeometry()?.getType()

      if (geometryName === 'Point') {
        this.dojouMeshSource.removeFeature(feature)
      }
    })

    dojouMeshStore.mesh.items.forEach(item => {
      const textStyle = new Style({
        text: new Text({
          text: item.val.toString(),
        }),
      })

      const { longitude, latitude } = item
      const textFeature = new Feature({
        geometry: new Point(transform([longitude, latitude], 'EPSG:4326', 'EPSG:3857')),
      })

      textFeature.setStyle(textStyle)

      this.dojouMeshSource.addFeature(textFeature)
    })
  }

  highlightIcon(id: string) {
    if (this.highlightTimer) {
      clearInterval(this.highlightTimer)
      this.iconHighlightSource.getFeatures().forEach(f => {
        this.iconHighlightSource.removeFeature(f)
      })
    }

    const duration = 2000
    const start = Date.now()
    const feature = this.iconSource.getFeatureById(id)
    const hFeature = new Feature({
      geometry: feature.getGeometry()?.clone(),
    })
    this.iconHighlightSource.addFeature(hFeature)
    const animate = () => {
      const elapsed = Date.now() - start

      if (elapsed >= duration) {
        clearInterval(this.highlightTimer)
        this.iconHighlightSource.removeFeature(hFeature)

        return
      }

      const elapsedRatio = elapsed / duration
      const radius = easeOut(elapsedRatio) * 25 + 5
      const opacity = 1 - elapsedRatio

      const style = new Style({
        image: new CircleStyle({
          radius: radius,
          stroke: new Stroke({
            color: `rgba(0, 255, 0, ${opacity})`,
          }),
          fill: new Fill({
            color: `rgba(0, 255, 0, ${opacity})`,
          }),
        }),
      })

      this.iconHighlightLayer.setStyle(style)
      this.map?.render()
    }
    this.highlightTimer = setInterval(animate, 10)
  }
}
